import React, { useContext } from 'react'
import { getOrderedAddressFields } from '@jarvis.oddle/core/tools/address'
import {
  CardGroup,
  CardHeader,
  CheckBox,
  InputContainer,
  Section
} from 'src/components/molecules/CheckoutComponents/Elements'
import Input from '@jarvis.oddle/core/components/Input'
import { combineString, isEmpty } from 'src/utils/string'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import Divider from '@jarvis.oddle/core/components/Divider'
import {
  margin,
  marginSizes,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Div from '@jarvis.oddle/core/components/Div'
import { RecipientInputsContainer } from '../ContactDetails'
import Flex from '@jarvis.oddle/core/components/Flex'
import { addressConfig } from '../BillingAddress'
import styled from 'styled-components'
import { Column } from '@jarvis.oddle/core/components/Grid'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { P } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { useRouter } from 'next/router'

const StyledNameInput = styled(Column)`
  margin-bottom: ${margin[marginSizes.small]};
  ${breakpoint('tablet')`
    padding-left: 8px;
    padding-right: 8px;
  `}
`

const StyledEmailPhoneSection = styled(CardGroup)`
  margin-bottom: ${margin[marginSizes.none]};
`

const StyledEmailInput = styled(InputContainer)`
  margin-bottom: ${margin[marginSizes.medium]};
`

const StyledSubTitle = styled(P)`
  margin-bottom: ${margin[marginSizes.medium]};
`

interface ContactDetailsProps {
  user: {
    data: {
      firstName: string
      lastName: string
      phone: string
      email: string
    }
  }
  nonTW: boolean
  addressFormat: string
  addressRequiredFields: string
  isPickup: boolean
}

const ContactDetails = ({
  user,
  nonTW,
  addressFormat,
  addressRequiredFields,
  isPickup
}: ContactDetailsProps): JSX.Element => {
  const router = useRouter()
  const { t } = useContext(TranslationContext)
  const orderedAddressFields = getOrderedAddressFields(
    addressFormat,
    addressRequiredFields
  )
  const { values } = useFormContext()
  const { query: { locale } } = router

  return (
    <Section>
      <CardHeader bold>{t('Contact Details')}</CardHeader>
      <CardGroup>
        <StyledNameInput>
          <Input
            type='text'
            label={t('Name')}
            value={combineString(user.data.firstName, user.data.lastName)}
            disabled
            name='name'
          />
        </StyledNameInput>
      </CardGroup>
      <StyledEmailPhoneSection>
        <StyledEmailInput>
          <Input
            type='text'
            label={t('Email')}
            value={user.data.email}
            disabled
            name='email'
          />
        </StyledEmailInput>
        <InputContainer>
          <Form.Phone
            type='phone'
            name='phone'
            label={t('Phone Number')}
            value={user.data.phone}
            required
            disabled={!isEmpty(user.data.phone)}
          />
        </InputContainer>
      </StyledEmailPhoneSection>
      <CardGroup>
        {nonTW && (
          <StyledNameInput>
            <Form.Input
              type='text'
              name='organisationName'
              label={t('Company Name (Optional)')}
              required
            />
          </StyledNameInput>
        )}
      </CardGroup>
      <Divider />
      <StyledSubTitle bold>{t('Recipients')}</StyledSubTitle>
      <Div width='100%' paddingBottom={paddingSizes.small}>
        <CheckBox
          name='recipientCheckbox'
          content={t('This is for my friends or family')}
        />
      </Div>
      {values.recipientCheckbox === true && (
        <RecipientInputsContainer>
          <Div width='100%' marginRight={marginSizes.small}>
            <Form.Input
              type='text'
              name='recipientName'
              label={t('Recipient Name')}
              required
            />
          </Div>
          <Div width='100%'>
            <Form.Phone
              type='phone'
              name='recipientContact'
              label={t('Recipient Phone')}
              required
              defaultCountryCode={(locale as string)?.split('_')?.[1]?.toLowerCase() || 'sg'}
            />
          </Div>
        </RecipientInputsContainer>
      )}
      {!isPickup && nonTW && (
        <React.Fragment>
          <React.Fragment>
            <StyledSubTitle bold>{t('Billing Address')}</StyledSubTitle>
            <Div width='100%' paddingBottom={paddingSizes.small}>
              <CheckBox
                name='billingAddressDifferentCheckbox'
                content={t('Billing address is different as delivery address')}
              />
            </Div>
          </React.Fragment>
          {values.billingAddressDifferentCheckbox === true && (
            <Flex flexDirection='column'>
              {orderedAddressFields.map((item, idx) => {
                const config = addressConfig[item.field](t)
                const label = item.isRequired ? config.heading : config.optional
                const name = config.billingFieldName
                return (
                  <Form.Input
                    key={idx}
                    type='text'
                    label={label}
                    name={name}
                    disabled={item.field === 'country'}
                  />
                )
              })}
            </Flex>
          )}
        </React.Fragment>
      )}
    </Section>
  )
}

export default ContactDetails
